'use client';
import React, { useState } from 'react';

import * as Styled from './styled';
import * as Icon from '@/ui/Icon';
import Button from '@/ui/Button';
import useDataframeContext from '../../../DataframeContext';
import { IDataFrameField, codeLanguageOptions } from '@/lib/dataFrameTypes';

import { ROOT_LAYOUT_DOM_NODE_ID } from '@/config/appSettings';
import { useModal } from 'react-hooks-use-modal';
import { useForm } from 'react-hook-form';
// import { DevTool } from '@hookform/devtools';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';

import { Pencil1Icon, ResetIcon, TrashIcon } from '@radix-ui/react-icons';

import { Row } from '@/ui/primatives';
import Input from '@/components/Input';

const dataTypes = [
  'str',
  'bool',
  // 'uint8',
  // 'u16',
  'u32',
  'u64',
  // 'i8',
  // 'i16',
  'i32',
  'i64',
  'f32',
  'f64',
  'date',
  'time',
  // 'list[bool]',
  // 'list[uint8]',
  // 'list[u16]',
  // 'list[u32]',
  // 'list[u64]',
  // 'list[i8]',
  // 'list[i16]',
  // 'list[i32]',
  // 'list[i64]',
  // 'list[f32]',
  // 'list[f64]',
  // 'list[str]',
  // 'list[date]',
  // 'list[time]',
];

const renderFunctions = ['image', 'link', 'none', 'code', 'markdown'];

const ModalWrapper: React.FC<{
  close: () => void;
  title: string;
  children: React.ReactNode;
}> = ({ close, title, children }) => {
  return (
    <Styled.Modal>
      <div className="header">
        <h6>{title}</h6>
        <Styled.ModalClose>
          <Icon.X size="16px" onClick={close} />
        </Styled.ModalClose>
      </div>
      {children}
    </Styled.Modal>
  );
};

const ColumnForm: React.FC<{
  values: { fieldName: string; dataType: string; renderFunc: string; codeLanguage?: string };
  onSubmit: (data: {
    fieldName: string;
    dataType: string;
    renderFunc: string;
    codeLanguage?: string;
  }) => Promise<boolean>;
  buttonText: string;
}> = ({ values, onSubmit, buttonText = 'Submit' }) => {
  const formSchema = Yup.object().shape({
    fieldName: Yup.string().required('Required'),
    dataType: Yup.string().required('Required'),
    renderFunc: Yup.string().required('Required'),
    codeLanguage: Yup.string(),
  });

  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(formSchema),
    defaultValues: {
      fieldName: values.fieldName || '',
      dataType: values.dataType || '',
      renderFunc: values.renderFunc || '',
      codeLanguage: values.codeLanguage || '',
    },
  });

  const isCode = watch('renderFunc') === 'code';
  const codeLanguage = watch('codeLanguage');

  console.log('codeLanguage', codeLanguage);

  // console.log('renderFunc', renderFunc);

  const clearForm = (e: React.MouseEvent<HTMLButtonElement>) => {
    reset();
    e.preventDefault();
  };

  const doSubmit = async (data: { fieldName: string; dataType: string; renderFunc: string }) => {
    const resp = await onSubmit(data);
    if (resp === true) {
      reset();
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit(doSubmit)}>
        <Input
          name="fieldName"
          label="Field name"
          placeholder="Field name"
          submit={register}
          error={errors.fieldName}
        />
        <Input.Select
          name="dataType"
          label="Data type"
          placeholder="Data type"
          control={control}
          options={dataTypes.map((type) => ({ label: type, value: type }))}
          error={errors.dataType}
        />
        <Input.Select
          name="renderFunc"
          label="Render function"
          placeholder="Render function"
          control={control}
          options={renderFunctions.map((type) => ({ label: type, value: type }))}
          error={errors.renderFunc}
        />

        {isCode && (
          <Input.Select label="Language" control={control} name="codeLanguage" options={codeLanguageOptions} />
        )}

        <Row gap={10}>
          <Button.Secondary small onClick={clearForm}>
            Clear
          </Button.Secondary>
          <Button.Basic small>{buttonText}</Button.Basic>
        </Row>
      </form>
      {/* <DevTool control={control} /> */}
    </>
  );
};

export const AddColumn: React.FC<{}> = ({}) => {
  const [serverError, setServerError] = useState<string | undefined>(undefined);
  const { addColumn } = useDataframeContext();

  const [Modal, open, close, isOpen] = useModal(ROOT_LAYOUT_DOM_NODE_ID, {
    preventScroll: true,
  });

  const onSubmit = async (data: { fieldName: string; dataType: string }) => {
    setServerError(undefined);
    const resp = await addColumn({ name: data.fieldName, dtype: data.dataType });
    if (resp === false) {
      setServerError('Failed to add column');
      return false;
    } else {
      close();
      return true;
    }
  };

  return (
    <>
      <div>
        <Button.Basic tiny onClick={open}>
          <Icon.Add height={12} /> Add Column
        </Button.Basic>
      </div>
      {isOpen && (
        <Modal>
          <ModalWrapper close={close} title="Add Column">
            {serverError && <div style={{ color: 'red' }}>{serverError}</div>}
            <ColumnForm
              values={{ fieldName: '', dataType: '', renderFunc: 'none' }}
              onSubmit={onSubmit}
              buttonText="Add"
            />
          </ModalWrapper>
        </Modal>
      )}
    </>
  );
};

export const UpdateColumn: React.FC<{ field: IDataFrameField }> = ({ field }) => {
  const [serverError, setServerError] = useState<string | undefined>(undefined);
  const { updateColumn } = useDataframeContext();

  const [Modal, open, close, isOpen] = useModal(ROOT_LAYOUT_DOM_NODE_ID, {
    preventScroll: true,
  });

  const onSubmit = async (data: { fieldName: string; dataType: string; renderFunc: string; codeLanguage?: string }) => {
    setServerError(undefined);
    const resp = await updateColumn(
      field.name,
      {
        name: data.fieldName,
        dtype: data.dataType,
      },
      data.renderFunc,
      data.codeLanguage,
    );
    if (resp === false) {
      setServerError('Failed to update column');
      return false;
    } else {
      close();
      return true;
    }
  };

  return (
    <>
      <Pencil1Icon onClick={open} />
      {isOpen && (
        <Modal>
          <ModalWrapper close={close} title="Update Column">
            {serverError && <div style={{ color: 'red' }}>{serverError}</div>}
            <ColumnForm
              values={{
                fieldName: field.name,
                dataType: field.dtype,
                renderFunc: field.metadata?._oxen.render.func || 'none',
                codeLanguage: field.metadata?._oxen.render.language,
              }}
              onSubmit={onSubmit}
              buttonText="Update"
            />
          </ModalWrapper>
        </Modal>
      )}
    </>
  );
};

export const DeleteColumn: React.FC<{ field: IDataFrameField }> = ({ field }) => {
  const { removeColumn } = useDataframeContext();
  return <TrashIcon onClick={() => removeColumn(field.name)} />;
};

export const RestoreColumn: React.FC<{ field: IDataFrameField }> = ({ field }) => {
  const { restoreColumn } = useDataframeContext();
  return (
    <span title={`Restore "${field.name}" column`}>
      <ResetIcon onClick={() => restoreColumn(field.name)} />
    </span>
  );
};
